import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import Tooltip from '@mui/material/Tooltip'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import StarIcon from '@mui/icons-material/Star'
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import StatusChip from '../../component/StatusChip'
import { buildListSearch as buildListSearchQuery, createInitialListState, parseNumberParam } from '../../utils/listPageState'
import { renderDateRange } from '../../utils/dateDisplay'
import { formatNumber } from '../../utils/numberDisplay'

const getMeetType2Options = (meetType1) => {
    if (String(meetType1) === '3') {
        return [
            { id: 1, name: '애니멀런' },
            { id: 2, name: '댕댕이레이스' },
            { id: 3, name: '산넘어산' },
            { id: 4, name: 'B급마라톤' },
        ]
    }

    return [
        { id: 1, name: '마라톤' },
        { id: 2, name: '트레일러닝' },
        { id: 3, name: '버추얼레이스' },
    ]
}

const DEFAULT_SEARCH_SELECT = {
    searchMeetType1: 3,
    searchMeetType2: '',
    searchStatus: '',
}

const SEARCH_SELECT_PARAMS = {
    searchMeetType1: {
        param: 'meet_type_1',
        defaultValue: DEFAULT_SEARCH_SELECT.searchMeetType1,
        parse: (value, defaultValue) => parseNumberParam(value, defaultValue),
    },
    searchMeetType2: {
        param: 'meet_type_2',
        defaultValue: DEFAULT_SEARCH_SELECT.searchMeetType2,
        parse: (value, defaultValue) => value === null || value === undefined || value === '' ? defaultValue : Number(value),
    },
    searchStatus: {
        param: 'status',
        defaultValue: DEFAULT_SEARCH_SELECT.searchStatus,
        parse: (value, defaultValue) => value === null || value === undefined || value === '' ? defaultValue : Number(value),
    },
}

const getBestProductListTotalCount = (count) => {
    if (Array.isArray(count)) {
        return count.length
    }

    return Number(count || 0)
}

const bestProductThumbnailPlaceholderSx = {
    width: 32,
    height: 32,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
    color: 'text.disabled',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    verticalAlign: 'middle',
}

const getBestProductThumbnailSrc = (row) => {
    const thumbnail = row.thumbnail ? String(row.thumbnail) : ''

    if (!thumbnail) {
        return ''
    }

    if (thumbnail.includes('animal')) {
        return `${process.env.REACT_APP_AWS_S3_URL}/${thumbnail}`
    }

    if (!row.adminId) {
        return ''
    }

    return `${process.env.REACT_APP_SERVER_URL}/product/${row.adminId}/${thumbnail}`
}

const renderBestProductThumbnail = (row) => {
    const thumbnailSrc = getBestProductThumbnailSrc(row)

    if (!thumbnailSrc) {
        return (
            <Box sx={bestProductThumbnailPlaceholderSx} aria-label="상품 이미지 없음">
                <ImageNotSupportedIcon sx={{ fontSize: 18 }} />
            </Box>
        )
    }

    return <img className="listImg" src={thumbnailSrc} alt={row.title || '상품 이미지'} />
}

const renderDdayChip = (dDay) => {
    const day = Number(dDay)

    if (!Number.isFinite(day)) {
        return <StatusChip label="-" variant="outlined" />
    }

    if (day > 0) {
        return <StatusChip label={`D+${day}`} variant="outlined" />
    }

    if (day === 0) {
        return <StatusChip label="D-Day" color="warning" />
    }

    return <StatusChip label={`D${day}`} color="info" />
}

const renderBestStatusChip = (productBest) => {
    if (productBest) {
        return <StatusChip label="베스트" color="warning" icon={<StarIcon />} />
    }

    return <StatusChip label="일반" variant="outlined" />
}

export default function BestProducts() {
    const location = useLocation()
    const history = useHistory()
    const actionInFlightRef = useRef(new Set())
    const initialListStateRef = useRef(createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    }))
    const initialListState = initialListStateRef.current
    const silentListRefreshRef = useRef(false)
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(null)
    const [totalCount, setTotalCount] = useState(0)
    const [data, setData] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')

    const isBusy = Boolean(actionLoading)
    const meetType2Options = getMeetType2Options(searchSelect.searchMeetType1)

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/productBest/getProductBestList', {
                page: curPage,
                offset: offset,
                searchText: searchQuery,
                searchSelect: searchSelect
            })

            if (data.code === 1) {
                setTotalCount(getBestProductListTotalCount(data.data?.count))
                setData(data.data?.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '베스트 상품 목록을 불러오지 못했습니다.'

                setTotalCount(0)
                setData([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '베스트 상품 목록을 불러오지 못했습니다.'

            setTotalCount(0)
            setData([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, history, searchQuery, searchSelect])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearchQuery({
            curPage,
            searchQuery,
            searchSelect,
            selectParams: SEARCH_SELECT_PARAMS,
        })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const updateSearchSelect = (e) => {
        if (isBusy) {
            return
        }

        if (curPage === 1) {
            silentListRefreshRef.current = true
        }

        const nextSearchSelect = {
            ...searchSelect,
            [e.target.name]: e.target.value
        }

        if (e.target.name === 'searchMeetType1') {
            nextSearchSelect.searchMeetType2 = ''
        }

        setSearchSelect(nextSearchSelect)
        setSearchQuery((searchText || '').trim())
        setCurPage(1)
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        if (isBusy) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        if (isBusy) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData(false)
            return
        }

        setSearchQuery(trimmedSearchText)

        if (curPage !== 1) {
            setCurPage(1)
            return
        }

        if (searchQuery !== trimmedSearchText) {
            return
        }

        getData(false)
    }

    const handleBestAction = async ({ key, request, successMessage, shouldMovePrevPage }) => {
        if (actionInFlightRef.current.has(key)) {
            return
        }

        try {
            actionInFlightRef.current.add(key)
            setActionLoading(key)

            const { data: response } = await request()

            if (response.code === 1) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar(successMessage)

                if (shouldMovePrevPage && curPage > 1 && data.length === 1) {
                    setCurPage((prev) => prev - 1)
                } else {
                    await getData(false)
                }
            } else if (response.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(response.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('베스트 상품 상태를 변경하지 못했습니다.')
        } finally {
            actionInFlightRef.current.delete(key)
            setActionLoading(null)
        }
    }

    const handleBestAdd = async (productId) => {
        await handleBestAction({
            key: `add-${productId}`,
            request: () => API.post('/api/v1/admin/productBest/create', { productId: productId }),
            successMessage: '베스트 상품에 추가했습니다.',
            shouldMovePrevPage: String(searchSelect.searchStatus) === '2'
        })
    }

    const handleBestRemove = async (productBestId) => {
        await handleBestAction({
            key: `remove-${productBestId}`,
            request: () => API.post('/api/v1/admin/productBest/delete', { productBestId: productBestId }),
            successMessage: '베스트 상품에서 해제했습니다.',
            shouldMovePrevPage: String(searchSelect.searchStatus) === '1'
        })
    }

    // 상태 필터의 "전체"는 기존 기본값('')과 메뉴 값(0)을 같은 의미로 본다.
    const normalizeDefaultValue = (key, value) => {
        if (key === 'searchStatus' && (value === 0 || value === '0')) {
            return ''
        }

        return value
    }

    // 검색어와 선택 필터가 기본값에서 벗어났는지 확인해 빈 상태 안내를 구분한다.
    const hasActiveFilters = () => {
        if ((searchQuery || '').trim()) {
            return true
        }

        return Object.entries(DEFAULT_SEARCH_SELECT).some(([key, defaultValue]) => {
            const currentValue = normalizeDefaultValue(key, searchSelect?.[key] ?? defaultValue)
            const normalizedDefaultValue = normalizeDefaultValue(key, defaultValue)

            return String(currentValue) !== String(normalizedDefaultValue)
        })
    }

    // 목록 행이 없을 때 오류, 필터 결과 없음, 기본 빈 목록을 명확히 보여준다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="베스트 상품 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button type="button" size="small" variant="outlined" onClick={() => getData(false)} disabled={isBusy}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters()) {
            return (
                <ListEmptyState
                    title="조건에 맞는 베스트 상품이 없습니다."
                    description="검색어 또는 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={isBusy} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 베스트 상품이 없습니다."
                description="상품이 등록되면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Grid item xs={12}>
                    <Spiner/>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Grid>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>베스트 상품</Title>
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={isBusy} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="타이틀"
                                type="search"
                                name="searchText"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                disabled={isBusy}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={isBusy}><SearchIcon /></IconButton>
                            <Divider sx={{ height: 28, m: 0.5, marginRight: 2 }} orientation="vertical" />
                            <TextField
                                label="지역"
                                select
                                name="searchMeetType1"
                                value={searchSelect.searchMeetType1}
                                onChange={updateSearchSelect}
                                size="small"
                                sx={{ minWidth: 150 }}
                                disabled={isBusy}
                            >
                                <MenuItem value=''>전체</MenuItem>
                                <MenuItem value={1}>국내</MenuItem>
                                <MenuItem value={2}>해외</MenuItem>
                                <MenuItem value={3}>애니멀런</MenuItem>
                            </TextField>
                            <TextField
                                label="타입"
                                select
                                name="searchMeetType2"
                                value={searchSelect.searchMeetType2}
                                onChange={updateSearchSelect}
                                size="small"
                                sx={{ minWidth: 150 }}
                                disabled={isBusy}
                            >
                                <MenuItem value=''>전체</MenuItem>
                                {meetType2Options.map((row) => (
                                    <MenuItem value={row.id} key={row.id}>{row.name}</MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="상태"
                                select
                                name="searchStatus"
                                value={searchSelect.searchStatus}
                                onChange={updateSearchSelect}
                                size="small"
                                sx={{ minWidth: 150 }}
                                disabled={isBusy}
                            >
                                <MenuItem value={0}>전체</MenuItem>
                                <MenuItem value={1}>베스트</MenuItem>
                                <MenuItem value={2}>일반</MenuItem>
                            </TextField>
                        </ListFilterForm>

                    </ListFilterBar>

                    <ListTableContainer>
                    <Table size="small" sx={{ minWidth: 1040 }}>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>이미지</TableCell>
                                <TableCell>타이틀</TableCell>
                                <TableCell>지역</TableCell>
                                <TableCell>접수기간</TableCell>
                                <TableCell>대회일</TableCell>
                                <TableCell align="left">D-Day</TableCell>
                                <TableCell align="right">가격</TableCell>
                                <TableCell align="center">상태</TableCell>
                                <TableCell align="center">관리</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.length > 0 ? data.map((row) => (
                                <TableRow key={row.productId}>
                                    <TableCell>{row.rownum}</TableCell>
                                    <TableCell>{renderBestProductThumbnail(row)}</TableCell>
                                    <TableCell>{row.title}</TableCell>
                                    <TableCell>{row.country} {row.area}</TableCell>
                                    <TableCell>{row.startTime} ~ <br/>{row.endTime}</TableCell>
                                    <TableCell>{renderDateRange(row.beginTime, row.finishTime)}</TableCell>
                                    <TableCell align="left">{renderDdayChip(row.dDay)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.price)} ~</TableCell>
                                    <TableCell align="center">{renderBestStatusChip(row.product_best)}</TableCell>
                                    <TableCell align="center">
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                pointerEvents: isBusy ? 'none' : 'auto',
                                                opacity: isBusy ? 0.5 : 1,
                                            }}
                                        >
                                            {!row.product_best ?
                                                <Tooltip title="베스트 추가">
                                                    <span>
                                                        <IconButton aria-label="베스트 추가" onClick={() => handleBestAdd(row.productId)} disabled={isBusy}>
                                                            <AddIcon color="success"/>
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                :
                                                <Tooltip title="베스트 해제">
                                                    <span>
                                                        <IconButton aria-label="베스트 해제" onClick={() => handleBestRemove(row.product_best.productBestId)} disabled={isBusy}>
                                                            <RemoveIcon className="listDelete"/>
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            }
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={10}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
